// src/components/connector/ConnectorStatusCard.js
import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Box,
  Grid
} from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';
import BoltIcon from '@mui/icons-material/Bolt';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Helper function to get status color and icon
const getStatusInfo = (status) => {
  switch (status?.toLowerCase()) {
    case 'available':
      return {
        color: 'success',
        icon: <CheckCircleIcon />,
        bgColor: '#e8f5e8',
        borderColor: '#4caf50'
      };
    case 'occupied':
    case 'charging':
      return {
        color: 'primary',
        icon: <BoltIcon />,
        bgColor: '#e3f2fd',
        borderColor: '#2196f3'
      };
    case 'preparing':
      return {
        color: 'warning',
        icon: <AccessTimeIcon />,
        bgColor: '#fff3e0',
        borderColor: '#ff9800'
      };
    case 'faulted':
    case 'error':
      return {
        color: 'error',
        icon: <ErrorIcon />,
        bgColor: '#ffebee',
        borderColor: '#f44336'
      };
    case 'unavailable':
    case 'offline':
      return {
        color: 'default',
        icon: <FlashOffIcon />,
        bgColor: '#f5f5f5',
        borderColor: '#9e9e9e'
      };
    default:
      return {
        color: 'default',
        icon: <FlashOnIcon />,
        bgColor: '#f5f5f5',
        borderColor: '#9e9e9e'
      };
  }
};

// Helper function to format connector type
const formatConnectorType = (type) => {
  if (!type) return 'Unknown';
  return type.toUpperCase();
};

// Helper function to format last update time
const formatLastUpdate = (timestamp) => {
  if (!timestamp) return 'Never';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const ConnectorStatusCard = ({ 
  connector, 
  isHighlighted = false, 
  showDetails = true,
  compact = false 
}) => {
  if (!connector) return null;

  const statusInfo = getStatusInfo(connector.ConnectorStatus);
  
  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        border: `2px solid ${isHighlighted ? '#1976d2' : statusInfo.borderColor}`,
        backgroundColor: isHighlighted ? '#e3f2fd' : statusInfo.bgColor,
        boxShadow: isHighlighted ? 3 : 1,
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent sx={{ p: compact ? 1.5 : 2 }}>
        {/* Header with connector number and status */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={compact ? 1 : 1.5}>
          <Box display="flex" alignItems="center">
            <EvStationIcon 
              sx={{ 
                mr: 0.5, 
                fontSize: compact ? 18 : 20,
                color: statusInfo.borderColor 
              }} 
            />
            <Typography 
              variant={compact ? "subtitle1" : "h6"} 
              fontWeight="bold"
              color="primary"
              sx={{ fontSize: compact ? '0.9rem' : '1.1rem' }}
            >
              {compact ? `C${connector.ConnectorId}` : `Connector ${connector.ConnectorId}`}
            </Typography>
          </Box>
          
          {isHighlighted && (
            <Chip 
              label={compact ? "Yours" : "Your Connector"} 
              color="primary" 
              size="small"
              sx={{ 
                fontWeight: 'bold',
                fontSize: compact ? '0.65rem' : '0.75rem',
                height: compact ? 20 : 24
              }}
            />
          )}
        </Box>

        {/* Status chip */}
        <Box mb={showDetails ? (compact ? 1 : 1.5) : 0.5}>
          <Chip
            icon={statusInfo.icon}
            label={connector.ConnectorStatus || 'Unknown'}
            color={statusInfo.color}
            size={compact ? "small" : "medium"}
            sx={{ 
              fontWeight: 'medium',
              fontSize: compact ? '0.7rem' : '0.8rem',
              height: compact ? 22 : 28
            }}
          />
        </Box>

        {/* Detailed information */}
        {showDetails && (
          <Grid container spacing={compact ? 0.5 : 1}>
            <Grid item xs={12}>
              <Typography 
                variant={compact ? "caption" : "body2"} 
                color="text.secondary"
                sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}
              >
                <strong>Type:</strong> {formatConnectorType(connector.ConnectorType)}
              </Typography>
            </Grid>
            
            {connector.ConnectorMaxVolt && (
              <Grid item xs={6}>
                <Typography 
                  variant={compact ? "caption" : "body2"} 
                  color="text.secondary"
                  sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}
                >
                  <strong>Voltage:</strong> {connector.ConnectorMaxVolt}V
                </Typography>
              </Grid>
            )}
            
            {connector.ConnectorMaxAmp && (
              <Grid item xs={6}>
                <Typography 
                  variant={compact ? "caption" : "body2"} 
                  color="text.secondary"
                  sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}
                >
                  <strong>Current:</strong> {connector.ConnectorMaxAmp}A
                </Typography>
              </Grid>
            )}
            
            <Grid item xs={6}>
              <Typography 
                variant={compact ? "caption" : "body2"} 
                color="text.secondary"
                sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}
              >
                <strong>Status:</strong> {connector.ConnectorEnabled ? 'Enabled' : 'Disabled'}
              </Typography>
            </Grid>
            
            {connector.last_status_update && (
              <Grid item xs={6}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: compact ? '0.65rem' : '0.7rem' }}
                >
                  {formatLastUpdate(connector.last_status_update)}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {/* Compact view - minimal info */}
        {!showDetails && (
          <Box>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}
            >
              {formatConnectorType(connector.ConnectorType)}
              {connector.ConnectorMaxVolt && ` • ${connector.ConnectorMaxVolt}V`}
              {!connector.ConnectorEnabled && ' • Disabled'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectorStatusCard;